import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
  Input,
} from '../../components/design-system';
import { ZoneMap } from '../../components/ui/ZoneMap';
import { geocodeAddress, type GeocodingResult } from '../../lib/maps';
import type { Destination, DestinationAlias, DestinationZone } from '../../types';

const ZONE_COLORS: Record<DestinationZone, string> = {
  city: '#FF993B',
  area: '#6F3FFD',
  greater: '#9EC98A',
};

function buildZones(d: Destination) {
  const zones: { label: string; radiusKm: number; color: string }[] = [];
  zones.push({ label: d.city_label, radiusKm: d.city_radius_km, color: ZONE_COLORS.city });
  if (d.area_radius_km != null && d.area_label) {
    zones.push({ label: d.area_label, radiusKm: d.area_radius_km, color: ZONE_COLORS.area });
  }
  if (d.greater_radius_km != null && d.greater_label) {
    zones.push({ label: d.greater_label, radiusKm: d.greater_radius_km, color: ZONE_COLORS.greater });
  }
  return zones;
}

interface DestinationDraft {
  name: string;
  country: string;
  latitude: number;
  longitude: number;
  city_radius_km: number;
  area_radius_km: number | null;
  greater_radius_km: number | null;
  city_label: string;
  area_label: string;
  greater_label: string;
  enableArea: boolean;
  enableGreater: boolean;
}

const EMPTY_DRAFT: DestinationDraft = {
  name: '',
  country: '',
  latitude: 0,
  longitude: 0,
  city_radius_km: 10,
  area_radius_km: 30,
  greater_radius_km: 60,
  city_label: 'City',
  area_label: 'Area',
  greater_label: 'Greater',
  enableArea: true,
  enableGreater: true,
};

function draftToZones(d: DestinationDraft) {
  const zones: { label: string; radiusKm: number; color: string }[] = [];
  zones.push({ label: d.city_label || 'City', radiusKm: d.city_radius_km || 10, color: ZONE_COLORS.city });
  if (d.enableArea && d.area_radius_km) {
    zones.push({ label: d.area_label || 'Area', radiusKm: d.area_radius_km, color: ZONE_COLORS.area });
  }
  if (d.enableGreater && d.greater_radius_km) {
    zones.push({ label: d.greater_label || 'Greater', radiusKm: d.greater_radius_km, color: ZONE_COLORS.greater });
  }
  return zones;
}

function draftFromDestination(d: Destination): DestinationDraft {
  return {
    name: d.name,
    country: d.country,
    latitude: d.latitude,
    longitude: d.longitude,
    city_radius_km: d.city_radius_km,
    area_radius_km: d.area_radius_km ?? 30,
    greater_radius_km: d.greater_radius_km ?? 60,
    city_label: d.city_label,
    area_label: d.area_label ?? 'Area',
    greater_label: d.greater_label ?? 'Greater',
    enableArea: d.area_radius_km != null,
    enableGreater: d.greater_radius_km != null,
  };
}

export function AdminDestinations() {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [aliases, setAliases] = useState<DestinationAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DestinationDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [geoQuery, setGeoQuery] = useState('');
  const [geoResults, setGeoResults] = useState<GeocodingResult[]>([]);
  const [geoSearching, setGeoSearching] = useState(false);

  const [newAlias, setNewAlias] = useState('');
  const [newAliasZone, setNewAliasZone] = useState<DestinationZone>('area');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  async function load() {
    const [{ data: destData }, { data: aliasData }] = await Promise.all([
      supabase.from('destinations').select('*').order('sort_order'),
      supabase.from('destination_aliases').select('*'),
    ]);
    setDestinations((destData ?? []) as Destination[]);
    setAliases((aliasData ?? []) as DestinationAlias[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function handleGeocode() {
    if (!geoQuery.trim()) return;
    setGeoSearching(true);
    const results = await geocodeAddress(geoQuery.trim());
    setGeoResults(results);
    setGeoSearching(false);
  }

  function selectGeoResult(r: GeocodingResult) {
    const parts = r.displayName.split(',').map((s) => s.trim());
    const country = parts[parts.length - 1] || '';
    const cityName = parts[0] || geoQuery;
    setDraft((d) => ({
      ...d,
      name: d.name || cityName,
      country: d.country || country,
      latitude: r.lat,
      longitude: r.lng,
    }));
    setGeoResults([]);
    setGeoQuery('');
  }

  async function handleSave() {
    if (!draft.name.trim() || !draft.latitude) {
      setError('Name and location are required.');
      return;
    }
    setSaving(true);
    setError(null);

    const slug = draft.name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const payload = {
      name: draft.name.trim(),
      slug,
      country: draft.country.trim(),
      latitude: draft.latitude,
      longitude: draft.longitude,
      city_radius_km: draft.city_radius_km,
      area_radius_km: draft.enableArea ? draft.area_radius_km : null,
      greater_radius_km: draft.enableGreater ? draft.greater_radius_km : null,
      city_label: draft.city_label.trim() || 'City',
      area_label: draft.enableArea ? (draft.area_label.trim() || 'Area') : null,
      greater_label: draft.enableGreater ? (draft.greater_label.trim() || 'Greater') : null,
      updated_at: new Date().toISOString(),
    };

    if (modalMode === 'edit' && editId) {
      const { error: err } = await supabase.from('destinations').update(payload).eq('id', editId);
      if (err) { setError(err.message); setSaving(false); return; }
    } else {
      const maxOrder = destinations.reduce((m, d) => Math.max(m, d.sort_order), -1);
      const { error: err } = await supabase.from('destinations').insert({ ...payload, sort_order: maxOrder + 1 });
      if (err) { setError(err.message); setSaving(false); return; }
    }

    setModalMode(null);
    setEditId(null);
    setDraft(EMPTY_DRAFT);
    setSaving(false);
    await load();
  }

  async function handleToggleActive(d: Destination) {
    await supabase.from('destinations').update({ active: !d.active, updated_at: new Date().toISOString() }).eq('id', d.id);
    await load();
  }

  async function handleDelete(id: string) {
    await supabase.from('destinations').delete().eq('id', id);
    await load();
  }

  async function handleAddAlias(destId: string) {
    if (!newAlias.trim()) return;
    await supabase.from('destination_aliases').insert({
      destination_id: destId,
      alias: newAlias.trim(),
      min_zone: newAliasZone,
    });
    setNewAlias('');
    await load();
  }

  async function handleDeleteAlias(aliasId: string) {
    await supabase.from('destination_aliases').delete().eq('id', aliasId);
    await load();
  }

  function openAdd() {
    setDraft(EMPTY_DRAFT);
    setEditId(null);
    setModalMode('add');
    setError(null);
    setGeoResults([]);
    setGeoQuery('');
  }

  function openEdit(d: Destination) {
    setDraft(draftFromDestination(d));
    setEditId(d.id);
    setModalMode('edit');
    setError(null);
    setGeoResults([]);
    setGeoQuery('');
  }

  const inputClass = 'rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none w-full';

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Destinations</h1>
            <p className="text-app-muted text-sm">Cities where Sekretly hosts operate. Each destination has concentric zones hosts can cover.</p>
          </div>
          <Button onClick={openAdd}>Add destination</Button>
        </div>

        {/* Search */}
        {destinations.length > 0 && (
          <div className="mb-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by city, country, or alias…"
              className={`${inputClass} max-w-sm`}
            />
          </div>
        )}

        {destinations.length === 0 && (
          <p className="text-sm text-app-muted text-center py-12">No destinations yet. Add your first city above.</p>
        )}

        <div className="flex flex-col gap-4">
          {destinations.filter((d) => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            const destAliasNames = aliases.filter((a) => a.destination_id === d.id).map((a) => a.alias.toLowerCase());
            return d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q) || destAliasNames.some((a) => a.includes(q));
          }).map((d) => {
            const destAliases = aliases.filter((a) => a.destination_id === d.id);
            const isExpanded = expandedId === d.id;
            const zones = buildZones(d);

            return (
              <div key={d.id} className={`bg-app-card border border-app-border rounded-2xl overflow-hidden ${!d.active ? 'opacity-50' : ''}`}>
                {/* Header row */}
                <div className="px-5 py-4 flex items-center gap-4">
                  <button onClick={() => setExpandedId(isExpanded ? null : d.id)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-app-fg">{d.name}</h3>
                        <p className="text-xs text-app-muted">{d.country}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {zones.map((z) => (
                        <span key={z.label} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border" style={{ color: z.color, borderColor: z.color + '40' }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: z.color }} />
                          {z.label} ({z.radiusKm}km)
                        </span>
                      ))}
                      {destAliases.length > 0 && (
                        <span className="text-[10px] text-app-muted">{destAliases.length} alias{destAliases.length !== 1 ? 'es' : ''}</span>
                      )}
                    </div>
                  </button>

                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEdit(d)} className="text-xs text-app-muted hover:text-app-fg transition-colors">Edit</button>
                    <button onClick={() => void handleToggleActive(d)} className={`text-xs transition-colors ${d.active ? 'text-crimson/60 hover:text-crimson' : 'text-success hover:text-success/80'}`}>
                      {d.active ? 'Deactivate' : 'Activate'}
                    </button>
                    {!d.active && (
                      <button onClick={() => void handleDelete(d.id)} className="text-xs text-crimson/60 hover:text-crimson transition-colors">Delete</button>
                    )}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                      className={`w-4 h-4 text-app-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-app-border px-5 py-5 flex flex-col gap-5">
                    {/* Map */}
                    <ZoneMap lat={d.latitude} lng={d.longitude} zones={zones} className="w-full h-72 rounded-xl overflow-hidden" />

                    {/* Zone legend */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {zones.map((z) => (
                        <div key={z.label} className="flex items-center gap-2 bg-app-bg border border-app-border rounded-lg px-3 py-2">
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: z.color }} />
                          <span className="text-sm text-app-fg font-medium">{z.label}</span>
                          <span className="text-xs text-app-muted ml-auto">{z.radiusKm} km</span>
                        </div>
                      ))}
                    </div>

                    {/* Aliases */}
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-app-muted font-medium mb-2">Aliases</h4>
                      <p className="text-xs text-app-muted mb-3">Names the chatbot recognises as part of this destination (e.g. suburbs, nearby towns).</p>

                      {destAliases.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {destAliases.map((a) => (
                            <span key={a.id} className="inline-flex items-center gap-1.5 bg-app-bg border border-app-border rounded-full px-3 py-1 text-sm">
                              <span className="text-app-fg">{a.alias}</span>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ZONE_COLORS[a.min_zone] }} />
                              <button onClick={() => void handleDeleteAlias(a.id)} className="text-app-muted hover:text-crimson ml-0.5">&times;</button>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 items-center">
                        <input
                          value={newAlias}
                          onChange={(e) => setNewAlias(e.target.value)}
                          placeholder="e.g. Cascais"
                          className={`${inputClass} max-w-[200px]`}
                          onKeyDown={(e) => { if (e.key === 'Enter') void handleAddAlias(d.id); }}
                        />
                        <select
                          value={newAliasZone}
                          onChange={(e) => setNewAliasZone(e.target.value as DestinationZone)}
                          className={`${inputClass} max-w-[120px]`}
                        >
                          <option value="city">{d.city_label}</option>
                          {d.area_label && <option value="area">{d.area_label}</option>}
                          {d.greater_label && <option value="greater">{d.greater_label}</option>}
                        </select>
                        <Button size="sm" onClick={() => void handleAddAlias(d.id)} disabled={!newAlias.trim()}>Add</Button>
                      </div>
                    </div>

                    {/* Coordinates */}
                    <p className="text-[10px] text-app-muted font-mono">
                      {d.latitude.toFixed(5)}, {d.longitude.toFixed(5)} &middot; slug: {d.slug}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add/Edit modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" onClick={() => { setModalMode(null); setEditId(null); }}>
          <div
            className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-dvh-92 overflow-y-auto pb-safe-or-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-app-fg">{modalMode === 'add' ? 'Add destination' : 'Edit destination'}</h3>
              <button onClick={() => { setModalMode(null); setEditId(null); }} className="w-8 h-8 rounded-full bg-app-bg border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            <div className="px-5 py-5 flex flex-col gap-5">
              {/* Geocode search */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-app-fg-2">Search city</label>
                <div className="flex gap-2">
                  <input
                    value={geoQuery}
                    onChange={(e) => setGeoQuery(e.target.value)}
                    placeholder="e.g. Lisbon, Porto, Barcelona…"
                    className={`flex-1 ${inputClass}`}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleGeocode(); }}
                  />
                  <Button size="sm" onClick={() => void handleGeocode()} disabled={geoSearching || !geoQuery.trim()}>
                    {geoSearching ? <Spinner size="sm" /> : 'Search'}
                  </Button>
                </div>
                {geoResults.length > 0 && (
                  <div className="bg-app-bg border border-app-border rounded-xl overflow-hidden">
                    {geoResults.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => selectGeoResult(r)}
                        className="w-full text-left px-3 py-2.5 text-sm text-app-fg hover:bg-black/5 transition-colors border-b border-app-border last:border-b-0"
                      >
                        {r.displayName}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Name + Country */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-app-fg-2">Name</label>
                  <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Lisbon" className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-app-fg-2">Country</label>
                  <input value={draft.country} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))} placeholder="Portugal" className={inputClass} />
                </div>
              </div>

              {/* Coordinates */}
              {draft.latitude !== 0 && (
                <p className="text-xs text-app-muted font-mono">
                  Coordinates: {draft.latitude.toFixed(5)}, {draft.longitude.toFixed(5)}
                </p>
              )}

              {/* Live map preview */}
              {draft.latitude !== 0 && (
                <ZoneMap lat={draft.latitude} lng={draft.longitude} zones={draftToZones(draft)} className="w-full h-56 rounded-xl overflow-hidden" />
              )}

              {/* Zone configuration */}
              <div className="flex flex-col gap-4">
                <h4 className="text-sm font-semibold text-app-fg-2">Zones</h4>

                {/* City — always on */}
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ZONE_COLORS.city }} />
                  <Input
                    value={draft.city_label}
                    onChange={(e) => setDraft((d) => ({ ...d, city_label: e.target.value }))}
                    placeholder="City"
                    className="flex-1"
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      type="number"
                      value={draft.city_radius_km}
                      onChange={(e) => setDraft((d) => ({ ...d, city_radius_km: Number(e.target.value) || 10 }))}
                      className={`${inputClass} w-20 text-center`}
                      min={1}
                    />
                    <span className="text-xs text-app-muted">km</span>
                  </div>
                </div>

                {/* Area — toggleable */}
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draft.enableArea}
                      onChange={(e) => setDraft((d) => ({ ...d, enableArea: e.target.checked }))}
                      className="rounded border-app-border bg-app-bg text-accent"
                    />
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ZONE_COLORS.area }} />
                    <span className="text-app-fg-2">Area zone</span>
                  </label>
                  {draft.enableArea && (
                    <div className="flex items-center gap-3 pl-7">
                      <Input
                        value={draft.area_label}
                        onChange={(e) => setDraft((d) => ({ ...d, area_label: e.target.value }))}
                        placeholder="Area"
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <input
                          type="number"
                          value={draft.area_radius_km ?? 30}
                          onChange={(e) => setDraft((d) => ({ ...d, area_radius_km: Number(e.target.value) || 30 }))}
                          className={`${inputClass} w-20 text-center`}
                          min={1}
                        />
                        <span className="text-xs text-app-muted">km</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Greater — toggleable */}
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draft.enableGreater}
                      onChange={(e) => setDraft((d) => ({ ...d, enableGreater: e.target.checked }))}
                      className="rounded border-app-border bg-app-bg text-accent"
                    />
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ZONE_COLORS.greater }} />
                    <span className="text-app-fg-2">Greater zone</span>
                  </label>
                  {draft.enableGreater && (
                    <div className="flex items-center gap-3 pl-7">
                      <Input
                        value={draft.greater_label}
                        onChange={(e) => setDraft((d) => ({ ...d, greater_label: e.target.value }))}
                        placeholder="Greater"
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <input
                          type="number"
                          value={draft.greater_radius_km ?? 60}
                          onChange={(e) => setDraft((d) => ({ ...d, greater_radius_km: Number(e.target.value) || 60 }))}
                          className={`${inputClass} w-20 text-center`}
                          min={1}
                        />
                        <span className="text-xs text-app-muted">km</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {error && <p className="text-xs text-crimson">{error}</p>}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-app-border">
                <button onClick={() => { setModalMode(null); setEditId(null); }} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
                <Button onClick={() => void handleSave()} disabled={saving || !draft.name.trim() || draft.latitude === 0}>
                  {saving ? <Spinner size="sm" className="text-ink" /> : modalMode === 'add' ? 'Add destination' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDestinations;
